import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Send,
  Mail,
  MessageSquare,
  Phone,
  Linkedin,
  Zap,
  Target,
  Users,
  BarChart3,
  TrendingUp,
  Clock,
  Calendar,
  Play,
  Pause,
  Settings,
  Plus,
  Filter,
  Search,
  Eye,
  Edit,
  Copy,
  Trash2,
  Activity,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../../../../components/ui/Button/Button';
import { Card } from '../../../../components/ui/Card';
import { Input } from '../../../../components/ui/Input';
import { cn } from '../../../../utils/cn';

// Import analytics components
import CampaignMetrics from '../../../email/analytics/CampaignMetrics';
import RecipientJourney from '../../../email/analytics/RecipientJourney';
import EngagementHeatmap from '../../../email/analytics/EngagementHeatmap';

interface OutreachTabProps {
  campaignData: any;
  onDataChange: (field: string, value: any) => void;
}

interface Campaign {
  id: string;
  name: string;
  type: 'email' | 'linkedin' | 'cold_call' | 'sms' | 'multi_channel';
  status: 'draft' | 'active' | 'paused' | 'completed';
  metrics: {
    sent: number;
    opened: number;
    clicked: number;
    replied: number;
    openRate: number;
    clickRate: number;
    replyRate: number;
  };
  lastActivity: string;
  nextStep: string;
}

interface Sequence {
  id: string;
  name: string;
  type: 'email' | 'linkedin' | 'multi_channel';
  steps: number;
  status: 'active' | 'paused' | 'draft';
  performance: {
    sent: number;
    opened: number;
    replied: number;
    openRate: number;
    replyRate: number;
  };
}

const campaignTypes = [
  { id: 'email', label: 'Email', icon: Mail, color: 'bg-blue-500' },
  { id: 'linkedin', label: 'LinkedIn', icon: Linkedin, color: 'bg-indigo-500' },
  { id: 'cold_call', label: 'Cold Call', icon: Phone, color: 'bg-green-500' },
  { id: 'sms', label: 'SMS', icon: MessageSquare, color: 'bg-purple-500' },
  { id: 'multi_channel', label: 'Multi-Channel', icon: Zap, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
];

const mockCampaigns: Campaign[] = [
  {
    id: '1',
    name: 'Q4 Product Launch',
    type: 'multi_channel',
    status: 'active',
    metrics: {
      sent: 1250,
      opened: 875,
      clicked: 312,
      replied: 89,
      openRate: 70.0,
      clickRate: 35.7,
      replyRate: 10.2
    },
    lastActivity: '2 hours ago',
    nextStep: 'Follow-up email scheduled'
  },
  {
    id: '2',
    name: 'Enterprise Outreach',
    type: 'email',
    status: 'active',
    metrics: {
      sent: 890,
      opened: 623,
      clicked: 187,
      replied: 45,
      openRate: 70.0,
      clickRate: 30.0,
      replyRate: 7.2
    },
    lastActivity: '1 day ago',
    nextStep: 'LinkedIn connection pending'
  }
];

const mockSequences: Sequence[] = [
  {
    id: '1',
    name: 'Cold Email Sequence',
    type: 'email',
    steps: 5,
    status: 'active',
    performance: {
      sent: 2340,
      opened: 1638,
      replied: 187,
      openRate: 70.0,
      replyRate: 8.0
    }
  },
  {
    id: '2',
    name: 'LinkedIn + Email Combo',
    type: 'multi_channel',
    steps: 7,
    status: 'active',
    performance: {
      sent: 1890,
      opened: 1323,
      replied: 151,
      openRate: 70.0,
      replyRate: 8.0
    }
  }
];

export const OutreachTab: React.FC<OutreachTabProps> = ({ campaignData, onDataChange }) => {
  const [activeView, setActiveView] = useState<'overview' | 'campaigns' | 'sequences' | 'analytics'>('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState<string | null>(null);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400';
      case 'paused':
        return 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400';
      case 'draft':
        return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300';
      case 'completed':
        return 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300';
    }
  };

  const getTypeIcon = (type: string) => {
    const typeConfig = campaignTypes.find(t => t.id === type);
    return typeConfig ? typeConfig.icon : Mail;
  };

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-6">
        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Active Campaigns</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">12</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-lg flex items-center justify-center">
              <Target className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+15%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Total Sent</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">24.5K</p>
            </div>
            <div className="w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center justify-center">
              <Send className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+23%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Reply Rate</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">8.7%</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+2.1%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-neutral-600 dark:text-neutral-400">Revenue</p>
              <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">$127K</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600 dark:text-green-400">+18%</span>
            <span className="text-neutral-600 dark:text-neutral-400 ml-1">vs last month</span>
          </div>
        </Card>
      </div>

      {/* Recent Campaigns */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Recent Campaigns</h3>
          <Button variant="outline" size="sm" onClick={() => setActiveView('campaigns')}>
            View All
          </Button>
        </div>
        <div className="space-y-4">
          {mockCampaigns.slice(0, 3).map((campaign) => {
            const Icon = getTypeIcon(campaign.type);
            return (
              <div key={campaign.id} className="flex items-center justify-between p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 bg-white dark:bg-neutral-700 rounded-lg flex items-center justify-center">
                    <Icon className="w-5 h-5 text-neutral-600 dark:text-neutral-400" />
                  </div>
                  <div>
                    <h4 className="font-medium text-neutral-900 dark:text-neutral-100">{campaign.name}</h4>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{campaign.lastActivity}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                      {campaign.metrics.replyRate}% reply rate
                    </p>
                    <p className="text-xs text-neutral-600 dark:text-neutral-400">
                      {campaign.metrics.sent} sent
                    </p>
                  </div>
                  <span className={cn(
                    "px-2 py-1 rounded-full text-xs font-medium",
                    getStatusColor(campaign.status)
                  )}>
                    {campaign.status}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );

  const renderCampaigns = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">All Campaigns</h3>
        <div className="flex items-center space-x-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-neutral-400" />
            <Input
              placeholder="Search campaigns..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-64"
            />
          </div>
          <Button variant="outline" size="sm">
            <Filter className="w-4 h-4 mr-2" />
            Filter
          </Button>
          <Button variant="gradient" size="sm">
            <Plus className="w-4 h-4 mr-2" />
            New Campaign
          </Button>
        </div>
      </div>

      <div className="grid gap-6">
        {mockCampaigns.map((campaign) => {
          const Icon = getTypeIcon(campaign.type);
          return (
            <Card key={campaign.id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{campaign.name}</h4>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{campaign.nextStep}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium",
                    getStatusColor(campaign.status)
                  )}>
                    {campaign.status}
                  </span>
                  <Button variant="ghost" size="sm">
                    <Eye className="w-4 h-4" />
                  </Button>
                  <Button variant="ghost" size="sm">
                    <Edit className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{campaign.metrics.sent.toLocaleString()}</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{campaign.metrics.openRate}%</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Open Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{campaign.metrics.clickRate}%</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Click Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{campaign.metrics.replyRate}%</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Reply Rate</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderSequences = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Email Sequences</h3>
        <Button variant="gradient" size="sm">
          <Plus className="w-4 h-4 mr-2" />
          New Sequence
        </Button>
      </div>

      <div className="grid gap-6">
        {mockSequences.map((sequence) => {
          const Icon = getTypeIcon(sequence.type);
          return (
            <Card key={sequence.id} className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-neutral-100 dark:bg-neutral-800 rounded-lg flex items-center justify-center">
                    <Icon className="w-6 h-6 text-neutral-600 dark:text-neutral-400" />
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">{sequence.name}</h4>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400">{sequence.steps} steps</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <span className={cn(
                    "px-3 py-1 rounded-full text-sm font-medium",
                    getStatusColor(sequence.status)
                  )}>
                    {sequence.status}
                  </span>
                  <Button variant="ghost" size="sm">
                    <Settings className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100">{sequence.performance.sent.toLocaleString()}</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Total Sent</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{sequence.performance.openRate}%</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Open Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{sequence.performance.replyRate}%</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Reply Rate</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600 dark:text-green-400">{sequence.performance.replied}</p>
                  <p className="text-sm text-neutral-600 dark:text-neutral-400">Replies</p>
                </div>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );

  const renderAnalytics = () => (
    <div className="space-y-6">
      <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">Outreach Analytics</h3>
      
      {/* Campaign Metrics */}
      <Card className="p-6">
        <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Campaign Performance</h4>
        <CampaignMetrics campaignId={selectedCampaign || 'default'} />
      </Card>

      {/* Recipient Journey */}
      <Card className="p-6">
        <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Recipient Journey</h4>
        <RecipientJourney />
      </Card>

      {/* Engagement Heatmap */}
      <Card className="p-6">
        <h4 className="text-md font-semibold text-neutral-900 dark:text-neutral-100 mb-4">Engagement Heatmap</h4>
        <EngagementHeatmap campaignId={selectedCampaign || 'default'} />
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="flex items-center space-x-1 bg-neutral-100 dark:bg-neutral-800 p-1 rounded-lg">
        {[
          { id: 'overview', label: 'Overview', icon: BarChart3 },
          { id: 'campaigns', label: 'Campaigns', icon: Target },
          { id: 'sequences', label: 'Sequences', icon: Zap },
          { id: 'analytics', label: 'Analytics', icon: Activity }
        ].map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveView(tab.id as any)}
              className={cn(
                "flex items-center space-x-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200",
                activeView === tab.id
                  ? "bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 shadow-sm"
                  : "text-neutral-600 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-neutral-100"
              )}
            >
              <Icon className="w-4 h-4" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Content */}
      <motion.div
        key={activeView}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
      >
        {activeView === 'overview' && renderOverview()}
        {activeView === 'campaigns' && renderCampaigns()}
        {activeView === 'sequences' && renderSequences()}
        {activeView === 'analytics' && renderAnalytics()}
      </motion.div>
    </div>
  );
}; 